import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../services/firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  where,
  setDoc,
  onSnapshot,
  orderBy,
  query,
  addDoc,
  serverTimestamp
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { FaUserCircle, FaArrowLeft, FaPaperPlane } from 'react-icons/fa';
import ProposedDetails from '../components/ProposedDetails'

import '../styles/ChatPage.css'

function ChatPage() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [chatExists, setChatExists] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [usernames, setUsernames] = useState({}); // usernames of members of current group/chat
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const currentUser = getAuth().currentUser;
  const messagesEndRef = useRef(null);

  // ✅ Ensure chat document exists
  useEffect(() => {
    const ensureChatExists = async () => {
      const chatRef = doc(db, 'chats', groupId);
      const chatSnap = await getDoc(chatRef);

      if (!chatSnap.exists()) {
        await setDoc(chatRef, {
          chatId: groupId,
          groupId: groupId,
          lastMessage: '',
          lastUpdated: new Date()
        });
      }

      setChatExists(true);
    };

    ensureChatExists();
  }, [groupId]);

  // ✅ Fetch usernames from group document
  useEffect(() => {
    const fetchUsernames = async () => {
      const groupRef = doc(db, 'groups', groupId);
      const groupSnap = await getDoc(groupRef);

      if (!groupSnap.exists()) {
        console.warn('Group not found for groupId:', groupId);
        return;
      }

      const groupData = groupSnap.data();
      const memberStatus = groupData.memberStatus || {};
      const userIds = Object.keys(memberStatus);

      const newUsernames = {};

      const fetchPromises = userIds.map(async (userId) => {
        const userRef = doc(db, 'users_public', userId);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          newUsernames[userId] = userSnap.data().displayName || 'Unnamed';
        } else {
          newUsernames[userId] = 'Unknown User';
        }
      });

      await Promise.all(fetchPromises);
      setUsernames(newUsernames);
    };

    fetchUsernames();
  }, [groupId]);

  // ✅ Listen to messages
  useEffect(() => {
    if (!chatExists) return;

    const messagesRef = collection(db, 'chats', groupId, 'messages');
    const q = query(messagesRef, orderBy('timestamp'));

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const msgs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setMessages(msgs);

      // Save last read timestamp when new messages are received
      if (currentUser) {
        const readRef = doc(db, `chats/${groupId}/chat_reads/${currentUser.uid}`);
        await setDoc(readRef, {
          lastRead: serverTimestamp()
        }, { merge: true });
      }
    });

    return () => unsubscribe();
  }, [chatExists, groupId]);

  // ✅ Scroll to bottom on new message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);


  // ✅ Send message
  const handleSend = async () => {
    if (!newMessage.trim()) return;

    const messagesRef = collection(db, 'chats', groupId, 'messages');
    const chatRef = doc(db, 'chats', groupId);

    await addDoc(messagesRef, {
      senderId: currentUser.uid,
      text: newMessage,
      timestamp: serverTimestamp(),
      type: 'text'
    });

    await setDoc(chatRef, {
      lastMessage: newMessage,
      lastUpdated: serverTimestamp()
    }, { merge: true });

    setNewMessage('');
  };


  // 🔎 Find searchActivityId for this user + groupId + allowed statuses
  const handleGoToDetail = async () => {
    if (!currentUser) {
      // If your app has a login route, you can navigate there:
      // navigate('/login');
      window.alert('You need to be signed in to view details.');
      return;
    }

    if (!groupId) return;

    setIsDetailLoading(true);
    try {
      const activitiesRef = collection(db, 'search_activities');
      const qAct = query(
        activitiesRef,
        where('ownerId', '==', currentUser.uid),
        where('groupId', '==', groupId),
        where('status', 'in', ['pending', 'matched', 'confirmed', 'finished'])
      );

      const snap = await getDocs(qAct);

      if (snap.empty) {
        window.alert('No related search activity found for this chat.');
        return;
      }

      // If multiple match, pick the most recently updated/created
      const score = (d) => {
        const data = d.data();
        // Try common timestamp fields; adjust to your schema if different
        const ts =
          data.updatedAt?.toMillis?.() ??
          data.lastUpdated?.toMillis?.() ??
          data.createdAt?.toMillis?.() ??
          0;
        return ts;
      };

      const docs = [...snap.docs].sort((a, b) => score(b) - score(a));
      const searchActivityId = docs[0].id;

      navigate(`/search_detail/${searchActivityId}`);
    } catch (err) {
      console.error('Failed to open detail:', err);
      window.alert('Unable to open detail. Please try again.');
    } finally {
      setIsDetailLoading(false);
    }
  };


  return (
    <div className="chat-container">
      <div className="chat-header d-flex justify-content-between align-items-center px-3">
        <button className="btn btn-link text-white p-0" onClick={() => navigate(-1)}>
          <FaArrowLeft />
        </button>

        <h5 className="m-0 text-white">Group Chat</h5>


        <button
          className="btn btn-outline-light btn-sm"
          onClick={handleGoToDetail}
          disabled={isDetailLoading}
        >
          {isDetailLoading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              Opening…
            </>
          ) : (
            'Detail'
          )}
        </button>

      </div>

      {/* Bootstrap subheader (responsive: 2 rows on small, 1 row on md+) */}
      <ProposedDetails groupId={groupId} />

      <div className="chat-messages">
        {messages.map(msg => {
          const isCurrentUser = msg.senderId === currentUser?.uid;
          return (
            <div
              key={msg.id}
              className={`message-row ${isCurrentUser ? 'own' : ''}`}
            >
              {!isCurrentUser && (
                <div className="user-icon">
                  <FaUserCircle />
                </div>
              )}
              <div className="message-bubble">
                {!isCurrentUser && (
                  <div className="user-name">
                    {usernames[msg.senderId] || 'Unknown'}
                  </div>
                )}
                <div>{msg.text}</div>
                <div className="message-meta">
                  {msg.timestamp?.toDate().toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </div>
              </div>
              {isCurrentUser && (
                <div className="user-icon">
                  <FaUserCircle />
                </div>
              )}
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input">
        <input
          type="text"
          placeholder="Type your message..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSend()}
        />
        <button onClick={handleSend}>
          <FaPaperPlane />
        </button>
      </div>
    </div>
  );
}

export default ChatPage;